import React, { useCallback, useMemo, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  type AddPatientPharmacyRequest,
  type PatientPharmacyInfo,
  type UpdatePatientPharmacyRequest,
  useAddPatientPharmacyMutation,
  usePatientPharmaciesQuery,
  useUpdatePatientPharmacyMutation,
} from '../store/api';

import { SectionHeader } from './PatientInfoModal/shared';

interface PatientPharmaciesSectionProps {
  patientId: string;
  source: string;
  compact?: boolean;
}

interface PharmacyFormState {
  name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  fax: string;
  dosespot_pharmacy_id: string;
  is_primary: boolean;
}

const EMPTY_FORM: PharmacyFormState = {
  name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  fax: '',
  dosespot_pharmacy_id: '',
  is_primary: false,
};

const addButtonSx = {
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 500,
  p: '6px 0',
  minWidth: 0,
} as const;

const editButtonSx = {
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.2,
  minWidth: 0,
  p: 0,
  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
} as const;

const trimOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const pharmacyToForm = (pharmacy: PatientPharmacyInfo): PharmacyFormState => ({
  name: pharmacy.name,
  address_line1: pharmacy.address_line1 ?? '',
  address_line2: pharmacy.address_line2 ?? '',
  city: pharmacy.city ?? '',
  state: pharmacy.state ?? '',
  zip: pharmacy.zip ?? '',
  phone: pharmacy.phone ?? '',
  fax: pharmacy.fax ?? '',
  dosespot_pharmacy_id: pharmacy.dosespot_pharmacy_id ?? '',
  is_primary: pharmacy.is_primary ?? false,
});

const formToAddRequest = (form: PharmacyFormState): AddPatientPharmacyRequest => ({
  name: form.name.trim(),
  address_line1: trimOrNull(form.address_line1),
  address_line2: trimOrNull(form.address_line2),
  city: trimOrNull(form.city),
  state: trimOrNull(form.state),
  zip: trimOrNull(form.zip),
  phone: trimOrNull(form.phone),
  fax: trimOrNull(form.fax),
  dosespot_pharmacy_id: trimOrNull(form.dosespot_pharmacy_id),
  is_primary: form.is_primary,
});

const formToUpdateRequest = (form: PharmacyFormState): UpdatePatientPharmacyRequest => ({
  name: form.name.trim(),
  address_line1: trimOrNull(form.address_line1),
  address_line2: trimOrNull(form.address_line2),
  city: trimOrNull(form.city),
  state: trimOrNull(form.state),
  zip: trimOrNull(form.zip),
  phone: trimOrNull(form.phone),
  fax: trimOrNull(form.fax),
  dosespot_pharmacy_id: trimOrNull(form.dosespot_pharmacy_id),
  is_primary: form.is_primary,
});

const formatAddress = (pharmacy: PatientPharmacyInfo): string => {
  const cityStateZip = [pharmacy.city, pharmacy.state, pharmacy.zip].filter(Boolean).join(', ');
  return [pharmacy.address_line1, pharmacy.address_line2, cityStateZip].filter(Boolean).join(' · ');
};

export const PatientPharmaciesSection: React.FC<PatientPharmaciesSectionProps> = ({
  patientId,
  source,
  compact = false,
}) => {
  const { data, isLoading, isError, refetch } = usePatientPharmaciesQuery(
    { patientId },
    { skip: !patientId },
  );
  const [addPharmacy, { isLoading: isAdding }] = useAddPatientPharmacyMutation();
  const [updatePharmacy, { isLoading: isUpdating }] = useUpdatePatientPharmacyMutation();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PharmacyFormState>(EMPTY_FORM);
  const { enqueueSnackbar } = useSnackbar();
  const tracking = useEventTracking();

  const pharmacies = useMemo(() => data?.pharmacies ?? [], [data?.pharmacies]);
  const isSaving = isAdding || isUpdating;
  const canSave = form.name.trim().length > 0;

  const sortedPharmacies = useMemo(
    () =>
      [...pharmacies].sort((a, b) => Number(b.is_primary ?? false) - Number(a.is_primary ?? false)),
    [pharmacies],
  );

  const resetForm = useCallback(() => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }, []);

  const startAdd = useCallback(() => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }, []);

  const startEdit = useCallback((pharmacy: PatientPharmacyInfo) => {
    setEditingId(pharmacy.patient_pharmacy_id);
    setForm(pharmacyToForm(pharmacy));
    setShowForm(true);
  }, []);

  const setFormField = useCallback((field: keyof PharmacyFormState, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [field]:
        field === 'state' && typeof value === 'string'
          ? value
              .replace(/[^A-Za-z]/g, '')
              .toUpperCase()
              .slice(0, 2)
          : value,
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    try {
      if (editingId) {
        await updatePharmacy({
          patientId,
          pharmacyId: editingId,
          updatePatientPharmacyRequest: formToUpdateRequest(form),
        }).unwrap();
        tracking.patient.infoClinicalItemUpdated({
          patient_id: patientId,
          source,
          item_type: 'pharmacy',
        });
      } else {
        await addPharmacy({
          patientId,
          addPatientPharmacyRequest: formToAddRequest(form),
        }).unwrap();
        tracking.patient.infoClinicalItemAdded({
          patient_id: patientId,
          source,
          item_type: 'pharmacy',
        });
      }
      resetForm();
    } catch {
      enqueueSnackbar('Failed to save pharmacy', { variant: 'error' });
    }
  }, [
    addPharmacy,
    canSave,
    editingId,
    enqueueSnackbar,
    form,
    patientId,
    resetForm,
    source,
    tracking,
    updatePharmacy,
  ]);

  const makePreferred = useCallback(
    async (pharmacy: PatientPharmacyInfo) => {
      try {
        await updatePharmacy({
          patientId,
          pharmacyId: pharmacy.patient_pharmacy_id,
          // Backend PATCH uses Pydantic model_fields_set, so omitted fields
          // remain untouched and only explicit nulls clear values.
          updatePatientPharmacyRequest: { is_primary: true },
        }).unwrap();
        tracking.patient.infoClinicalItemUpdated({
          patient_id: patientId,
          source,
          item_type: 'pharmacy',
        });
      } catch {
        enqueueSnackbar('Failed to update preferred pharmacy', { variant: 'error' });
      }
    },
    [enqueueSnackbar, patientId, source, tracking, updatePharmacy],
  );

  return (
    <Box sx={{ mb: compact ? 0 : 2 }}>
      {!compact && <SectionHeader>PHARMACIES</SectionHeader>}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {isError && (
        <Box sx={{ py: 1 }}>
          <Button size="small" onClick={() => refetch()} sx={{ textTransform: 'none' }}>
            Retry
          </Button>
        </Box>
      )}

      {!isLoading && !isError && sortedPharmacies.length === 0 && !showForm && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No pharmacies on file
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {sortedPharmacies.map(pharmacy => {
          const address = formatAddress(pharmacy);
          return (
            <Box
              key={pharmacy.patient_pharmacy_id}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                py: 0.75,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600 }}>
                      {pharmacy.name}
                    </Typography>
                    {pharmacy.is_primary && (
                      <Chip size="small" variant="soft" color="primary" label="Preferred" />
                    )}
                    <Chip
                      size="small"
                      variant="soft"
                      color={pharmacy.is_dosespot_linked ? 'success' : 'default'}
                      label={pharmacy.is_dosespot_linked ? 'DoseSpot' : 'Local only'}
                    />
                    {pharmacy.sync_status === 'sync_failed' && (
                      <Chip size="small" variant="soft" color="error" label="Sync issue" />
                    )}
                  </Box>
                  {address && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.25 }}
                    >
                      {address}
                    </Typography>
                  )}
                  {(pharmacy.phone || pharmacy.fax) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {[
                        pharmacy.phone && `P ${pharmacy.phone}`,
                        pharmacy.fax && `F ${pharmacy.fax}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 0.25 }}>
                  {!pharmacy.is_primary && (
                    <Tooltip title="Make preferred">
                      <IconButton
                        size="small"
                        onClick={() => makePreferred(pharmacy)}
                        sx={{ p: 0.25 }}
                      >
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button
                    variant="text"
                    size="small"
                    color="primary"
                    sx={editButtonSx}
                    onClick={() => startEdit(pharmacy)}
                  >
                    Edit
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {showForm && (
        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          <TextField
            label="Pharmacy name"
            size="small"
            required
            value={form.name}
            onChange={event => setFormField('name', event.target.value)}
            sx={{ gridColumn: { sm: 'span 4' } }}
          />
          <TextField
            label="Address"
            size="small"
            value={form.address_line1}
            onChange={event => setFormField('address_line1', event.target.value)}
            sx={{ gridColumn: { sm: 'span 2' } }}
          />
          <TextField
            label="Address 2"
            size="small"
            value={form.address_line2}
            onChange={event => setFormField('address_line2', event.target.value)}
            sx={{ gridColumn: { sm: 'span 2' } }}
          />
          <TextField
            label="City"
            size="small"
            value={form.city}
            onChange={event => setFormField('city', event.target.value)}
          />
          <TextField
            label="State"
            size="small"
            value={form.state}
            onChange={event => setFormField('state', event.target.value)}
          />
          <TextField
            label="ZIP"
            size="small"
            value={form.zip}
            onChange={event => setFormField('zip', event.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.is_primary}
                onChange={event => setFormField('is_primary', event.target.checked)}
              />
            }
            label="Preferred"
            sx={{ m: 0 }}
          />
          <TextField
            label="Phone"
            size="small"
            value={form.phone}
            onChange={event => setFormField('phone', event.target.value)}
            sx={{ gridColumn: { sm: 'span 2' } }}
          />
          <TextField
            label="Fax"
            size="small"
            value={form.fax}
            onChange={event => setFormField('fax', event.target.value)}
            sx={{ gridColumn: { sm: 'span 2' } }}
          />
          <Box
            sx={{
              gridColumn: '1 / -1',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={resetForm}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!canSave || isSaving}
              onClick={handleSave}
              sx={{ textTransform: 'none' }}
            >
              {editingId ? 'Save' : 'Add'}
            </Button>
          </Box>
        </Box>
      )}

      {!showForm && !editingId && (
        <Button size="small" onClick={startAdd} sx={addButtonSx}>
          + Add pharmacy
        </Button>
      )}
    </Box>
  );
};
